// frontend/src/portal/pages/SuperAdminDashboard.jsx
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchSuperAdminDashboardStats, fetchSuperAdminRecentInvitations } from "../../api.js";
import { usePortalAuth } from "../components/RequireClient.jsx";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";

function formatRelativeTime(value) {
  if (!value) return "—";
  try {
    const date = new Date(value);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  } catch {
    return String(value);
  }
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function inviteStatusLabel(status) {
  if (!status) return "Unknown";
  switch (status) {
    case "pending":
      return "Pending";
    case "accepted":
      return "Accepted";
    case "expired":
      return "Expired";
    case "revoked":
      return "Revoked";
    default:
      return status;
  }
}

function inviteStatusStyle(status) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    fontSize: "12px",
    fontWeight: 600,
  };

  switch (status) {
    case "accepted":
      return { ...base, backgroundColor: "var(--success-bg)", color: "var(--success)" };
    case "pending":
      return { ...base, backgroundColor: "var(--warning-bg)", color: "var(--warning)" };
    case "expired":
    case "revoked":
      return { ...base, backgroundColor: "var(--surface-elevated)", color: "var(--text-muted)" };
    default:
      return { ...base, backgroundColor: "var(--surface-elevated)", color: "var(--text-muted)" };
  }
}

export default function SuperAdminDashboard() {
  const { memberRole } = usePortalAuth();
  const isSuperAdmin = memberRole === "super_admin";

  const [stats, setStats] = useState(null);
  const [recentInvites, setRecentInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showProvision, setShowProvision] = useState(false);
  const [provisionForm, setProvisionForm] = useState({
    tenantName: "",
    adminEmail: "",
    adminFirstName: "",
    adminLastName: "",
    seats: "2",
  });
  const [provisionBusy, setProvisionBusy] = useState(false);
  const [provisionError, setProvisionError] = useState(null);
  const [provisionSuccess, setProvisionSuccess] = useState(null);
  const [lastProvisionResult, setLastProvisionResult] = useState(null);

  const provisionRef = useRef(null);
  const [reloadToken, setReloadToken] = useState(0);

  const tenantStats = stats?.tenants || {};
  const userStats = stats?.users || {};
  const invitationStats = stats?.invitations || {};
  const recentActivity = stats?.recentActivity || [];
  const dbStats = stats?.database || [];

  useEffect(() => {
    if (!isSuperAdmin) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [dashboard, invites] = await Promise.all([fetchSuperAdminDashboardStats(), fetchSuperAdminRecentInvitations({ limit: 5 })]);

        if (cancelled) return;

        setStats(dashboard || null);
        setRecentInvites(invites?.invitations || []);
      } catch (err) {
        if (cancelled) return;
        console.error("[SuperAdminDashboard] Failed to load stats", err);
        setError(err?.message || "Failed to load dashboard data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, reloadToken]);

  const handleOpenProvision = () => {
    setShowProvision(true);
    setProvisionError(null);
    setProvisionSuccess(null);
    setLastProvisionResult(null);

    if (provisionRef.current) {
      provisionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleProvisionChange = (evt) => {
    const { name, value } = evt.target;
    setProvisionForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProvisionSubmit = async (evt) => {
    evt.preventDefault();
    setProvisionBusy(true);
    setProvisionError(null);
    setProvisionSuccess(null);
    setLastProvisionResult(null);

    const payload = {
      tenantName: provisionForm.tenantName.trim(),
      adminEmail: provisionForm.adminEmail.trim(),
      adminFirstName: provisionForm.adminFirstName.trim() || null,
      adminLastName: provisionForm.adminLastName.trim() || null,
      seats: provisionForm.seats && !Number.isNaN(Number(provisionForm.seats)) ? Number(provisionForm.seats) : 2,
    };

    if (!payload.tenantName || !payload.adminEmail) {
      setProvisionBusy(false);
      setProvisionError("Tenant name and admin email are required.");
      return;
    }

    try {
      const response = await fetch("/api/superadmin/tenants/provision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      let body = null;
      try {
        body = await response.json();
      } catch {
        body = null;
      }

      if (!response.ok) {
        const message = body?.detail || body?.title || body?.message || `Provisioning failed with status ${response.status}`;
        throw new Error(message);
      }

      setProvisionBusy(false);
      setLastProvisionResult(body || null);

      const tenantName = body?.tenant?.name || body?.tenant?.slug || payload.tenantName;
      const ownerEmail = body?.ownerUser?.email || payload.adminEmail;
      const clerkOrgId = body?.clerk?.clerkOrgId || body?.tenant?.clerk_org_id;

      const successMsgParts = [];
      successMsgParts.push(`Created tenant "${tenantName}"`);
      if (ownerEmail) successMsgParts.push(`invited ${ownerEmail}`);
      if (clerkOrgId) successMsgParts.push(`Clerk org: ${clerkOrgId}`);

      setProvisionSuccess(successMsgParts.join(" – "));
      setProvisionForm({
        tenantName: "",
        adminEmail: "",
        adminFirstName: "",
        adminLastName: "",
        seats: "2",
      });

      setReloadToken((x) => x + 1);
    } catch (err) {
      console.error("[SuperAdminDashboard] Provision failed", err);
      setProvisionBusy(false);
      setProvisionError(err?.message || "Failed to provision client");
    }
  };

  if (!isSuperAdmin) {
    return (
      <ErrorBoundary componentName="SuperAdminDashboard">
        <div style={{ minHeight: "100vh", background: "var(--bg-secondary)", padding: "var(--space-2xl)" }}>
          <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-2xl)",
                textAlign: "center",
                border: "1px solid var(--border-primary)",
              }}>
              <span style={{ fontSize: "64px" }}>🔒</span>
              <h1 style={{ fontSize: "24px", fontWeight: "600", color: "var(--text-primary)", margin: "var(--space-lg) 0 var(--space-sm)" }}>Access Denied</h1>
              <p style={{ color: "var(--text-secondary)", margin: 0 }}>You do not have permission to access the super admin dashboard.</p>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (loading && !stats && !error) {
    return (
      <ErrorBoundary componentName="SuperAdminDashboard">
        <div style={{ minHeight: "100vh", background: "var(--bg-secondary)", padding: "var(--space-2xl)" }}>
          <div style={{ maxWidth: "var(--container-max-wide)", margin: "0 auto" }}>
            <h1 style={{ fontSize: "28px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-xl)" }}>Super Admin Dashboard</h1>
            <div style={{ textAlign: "center", padding: "48px" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "3px solid var(--border-primary)",
                  borderTopColor: "var(--accent-primary)",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  margin: "0 auto var(--space-lg)",
                }}
              />
              <p style={{ color: "var(--text-muted)" }}>Loading dashboard data...</p>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary componentName="SuperAdminDashboard">
      <div style={{ minHeight: "100vh", background: "var(--bg-secondary)" }}>
        {/* Header */}
        <div style={{ background: "var(--accent-gradient)", borderBottom: "1px solid var(--border-secondary)" }}>
          <div style={{ maxWidth: "var(--container-max-wide)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
            <h1 style={{ fontSize: "32px", fontWeight: "400", color: "#fff", margin: "0 0 var(--space-sm) 0", letterSpacing: "-0.5px" }}>Super Admin Dashboard</h1>
            <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.9)", margin: 0 }}>CBCFX production overview, Clerk orgs & invitations, and system health</p>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: "var(--container-max-wide)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
          {error && (
            <div
              style={{
                backgroundColor: "var(--danger-bg)",
                border: "1px solid var(--danger)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-xl)",
              }}>
              <div style={{ fontWeight: "600", color: "var(--danger)", marginBottom: "var(--space-xs)" }}>Failed to load dashboard</div>
              <div style={{ color: "var(--danger)", fontSize: "14px" }}>{error}</div>
            </div>
          )}

          {/* Quick Actions */}
          <div style={{ marginBottom: "var(--space-2xl)" }}>
            <h2 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-lg)" }}>Quick Actions</h2>
            <div style={{ display: "flex", gap: "var(--space-md)", flexWrap: "wrap" }}>
              <Link
                to="/portal/ops/tenants"
                style={{
                  padding: "var(--space-md) var(--space-xl)",
                  backgroundColor: "var(--surface)",
                  color: "var(--text-primary)",
                  textDecoration: "none",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-primary)",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "all var(--transition-fast)",
                  display: "inline-block",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-accent)";
                  e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-primary)";
                  e.currentTarget.style.boxShadow = "none";
                }}>
                Manage Tenants
              </Link>
              <Link
                to="/portal/ops/audit"
                style={{
                  padding: "var(--space-md) var(--space-xl)",
                  backgroundColor: "var(--surface)",
                  color: "var(--text-primary)",
                  textDecoration: "none",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-primary)",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "all var(--transition-fast)",
                  display: "inline-block",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-accent)";
                  e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-primary)";
                  e.currentTarget.style.boxShadow = "none";
                }}>
                View Audit Log
              </Link>
              <button
                type="button"
                onClick={handleOpenProvision}
                style={{
                  padding: "var(--space-md) var(--space-xl)",
                  backgroundColor: "var(--accent-primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "var(--radius-md)",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all var(--transition-fast)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--accent-secondary)";
                  e.currentTarget.style.boxShadow = "var(--shadow-md)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--accent-primary)";
                  e.currentTarget.style.boxShadow = "none";
                }}>
                ⚡ Quick Provision Client
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-lg)", marginBottom: "var(--space-2xl)" }}>
            <StatCard title="Total Tenants" value={tenantStats.total} subtitle={`${tenantStats.active || 0} active • ${tenantStats.trial || 0} trial`} />
            <StatCard title="Total Users" value={userStats.total} subtitle={`Across ${userStats.tenantsWithUsers || 0} tenants`} color="success" />
            <StatCard
              title="Clerk Invitations"
              value={invitationStats.total}
              subtitle={`${invitationStats.pending || 0} pending • ${invitationStats.accepted || 0} accepted`}
              color="info"
            />
            <StatCard title="Database Tables" value={dbStats.length} subtitle="System health OK" color="muted" />
          </div>

          {/* Two Column Layout */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "var(--space-xl)", marginBottom: "var(--space-2xl)" }}>
            {/* Recent Activity */}
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                border: "1px solid var(--border-primary)",
                padding: "var(--space-xl)",
                boxShadow: "var(--shadow-sm)",
              }}>
              <h3 style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-lg)", margin: 0 }}>Recent Activity</h3>
              {recentActivity.length === 0 ? (
                <p style={{ color: "var(--text-muted)", fontSize: "14px", margin: "var(--space-lg) 0 0" }}>No recent activity</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                  {recentActivity.slice(0, 8).map((activity) => (
                    <div
                      key={activity.id}
                      style={{
                        padding: "var(--space-md) 0",
                        borderBottom: "1px solid var(--border-primary)",
                      }}>
                      <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: "500", marginBottom: "var(--space-xs)" }}>{activity.action}</div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                        {activity.tenantName ? `${activity.tenantName}` : "System"} • {formatRelativeTime(activity.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Invitations */}
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                border: "1px solid var(--border-primary)",
                padding: "var(--space-xl)",
                boxShadow: "var(--shadow-sm)",
              }}>
              <h3 style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-lg)", margin: 0 }}>Recent Clerk Invitations</h3>
              {recentInvites.length === 0 ? (
                <p style={{ color: "var(--text-muted)", fontSize: "14px", margin: "var(--space-lg) 0 0" }}>No recent invitations</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                  {recentInvites.map((invite) => (
                    <div
                      key={invite.id}
                      style={{
                        padding: "var(--space-md) 0",
                        borderBottom: "1px solid var(--border-primary)",
                      }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "var(--space-md)" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: "500", marginBottom: "var(--space-xs)" }}>{invite.email}</div>
                          <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{formatRelativeTime(invite.invitedAt)}</div>
                        </div>
                        <span style={inviteStatusStyle(invite.status)}>{inviteStatusLabel(invite.status)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Link
                to="/portal/admin/invite"
                style={{
                  display: "inline-block",
                  marginTop: "var(--space-lg)",
                  color: "var(--accent-primary)",
                  fontSize: "14px",
                  fontWeight: "500",
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}>
                View all invitations →
              </Link>
            </div>
          </div>

          {/* Quick Provision Form */}
          {showProvision && (
            <div
              ref={provisionRef}
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                border: "1px solid var(--border-accent)",
                padding: "var(--space-xl)",
                boxShadow: "var(--shadow-lg)",
                marginBottom: "var(--space-2xl)",
              }}>
              <h3 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-md)" }}>Quick Provision Client</h3>
              <p style={{ fontSize: "14px", color: "var(--text-secondary)", marginBottom: "var(--space-xl)" }}>
                Create a new tenant, Clerk organization, and send owner invitation
              </p>

              <form onSubmit={handleProvisionSubmit} style={{ display: "grid", gap: "var(--space-lg)", maxWidth: "600px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>
                    Tenant Name <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="tenantName"
                    value={provisionForm.tenantName}
                    onChange={handleProvisionChange}
                    required
                    disabled={provisionBusy}
                    style={{
                      width: "100%",
                      padding: "var(--space-md)",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "14px",
                      backgroundColor: "var(--surface)",
                      color: "var(--text-primary)",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>
                    Admin Email <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="adminEmail"
                    value={provisionForm.adminEmail}
                    onChange={handleProvisionChange}
                    required
                    disabled={provisionBusy}
                    style={{
                      width: "100%",
                      padding: "var(--space-md)",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "14px",
                      backgroundColor: "var(--surface)",
                      color: "var(--text-primary)",
                    }}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-md)" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>First Name</label>
                    <input
                      type="text"
                      name="adminFirstName"
                      value={provisionForm.adminFirstName}
                      onChange={handleProvisionChange}
                      disabled={provisionBusy}
                      style={{
                        width: "100%",
                        padding: "var(--space-md)",
                        border: "1px solid var(--border-primary)",
                        borderRadius: "var(--radius-md)",
                        fontSize: "14px",
                        backgroundColor: "var(--surface)",
                        color: "var(--text-primary)",
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>Last Name</label>
                    <input
                      type="text"
                      name="adminLastName"
                      value={provisionForm.adminLastName}
                      onChange={handleProvisionChange}
                      disabled={provisionBusy}
                      style={{
                        width: "100%",
                        padding: "var(--space-md)",
                        border: "1px solid var(--border-primary)",
                        borderRadius: "var(--radius-md)",
                        fontSize: "14px",
                        backgroundColor: "var(--surface)",
                        color: "var(--text-primary)",
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>Seats</label>
                  <input
                    type="number"
                    name="seats"
                    value={provisionForm.seats}
                    onChange={handleProvisionChange}
                    min="1"
                    disabled={provisionBusy}
                    style={{
                      width: "120px",
                      padding: "var(--space-md)",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "14px",
                      backgroundColor: "var(--surface)",
                      color: "var(--text-primary)",
                    }}
                  />
                </div>

                {provisionError && (
                  <div style={{ padding: "var(--space-md)", backgroundColor: "var(--danger-bg)", border: "1px solid var(--danger)", borderRadius: "var(--radius-sm)" }}>
                    <p style={{ color: "var(--danger)", fontSize: "14px", margin: 0 }}>{provisionError}</p>
                  </div>
                )}

                {provisionSuccess && (
                  <div style={{ padding: "var(--space-md)", backgroundColor: "var(--success-bg)", border: "1px solid var(--success)", borderRadius: "var(--radius-sm)" }}>
                    <p style={{ color: "var(--success)", fontSize: "14px", margin: 0, fontWeight: "500" }}>✓ {provisionSuccess}</p>
                  </div>
                )}

                <div style={{ display: "flex", gap: "var(--space-md)" }}>
                  <button
                    type="submit"
                    disabled={provisionBusy}
                    style={{
                      padding: "var(--space-md) var(--space-xl)",
                      backgroundColor: provisionBusy ? "var(--text-disabled)" : "var(--accent-primary)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "var(--radius-md)",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: provisionBusy ? "not-allowed" : "pointer",
                    }}>
                    {provisionBusy ? "Provisioning..." : "Provision Client"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowProvision(false)}
                    disabled={provisionBusy}
                    style={{
                      padding: "var(--space-md) var(--space-xl)",
                      backgroundColor: "var(--surface)",
                      color: "var(--text-secondary)",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: provisionBusy ? "not-allowed" : "pointer",
                    }}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Database Stats Table */}
          {dbStats.length > 0 && (
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                border: "1px solid var(--border-primary)",
                overflow: "hidden",
                boxShadow: "var(--shadow-sm)",
              }}>
              <div style={{ padding: "var(--space-xl)", borderBottom: "1px solid var(--border-primary)" }}>
                <h3 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", margin: 0 }}>Database Statistics</h3>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "var(--surface-elevated)", borderBottom: "1px solid var(--border-primary)" }}>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Table</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "right", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Rows</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "right", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Size (MB)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbStats.map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid var(--border-primary)" }}>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)", fontFamily: "monospace" }}>
                          {row.table || row.table_name || "—"}
                        </td>
                        <td style={{ padding: "var(--space-md)", textAlign: "right", fontSize: "14px", color: "var(--text-primary)" }}>
                          {(row.rows || row.table_rows)?.toLocaleString() || "—"}
                        </td>
                        <td style={{ padding: "var(--space-md)", textAlign: "right", fontSize: "14px", color: "var(--text-primary)" }}>{row.sizeMB || row.size_mb || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </ErrorBoundary>
  );
}

function StatCard({ title, value, subtitle, color = "primary" }) {
  const getColorStyles = () => {
    switch (color) {
      case "success":
        return { borderColor: "var(--success)", iconBg: "var(--success-bg)", iconColor: "var(--success)" };
      case "info":
        return { borderColor: "var(--info)", iconBg: "var(--info-bg)", iconColor: "var(--info)" };
      case "warning":
        return { borderColor: "var(--warning)", iconBg: "var(--warning-bg)", iconColor: "var(--warning)" };
      case "muted":
        return { borderColor: "var(--border-primary)", iconBg: "var(--surface-elevated)", iconColor: "var(--text-muted)" };
      default:
        return { borderColor: "var(--accent-primary)", iconBg: "var(--info-bg)", iconColor: "var(--accent-primary)" };
    }
  };

  const colors = getColorStyles();

  return (
    <div
      style={{
        backgroundColor: "var(--surface)",
        border: `1px solid ${colors.borderColor}`,
        borderRadius: "var(--radius-lg)",
        padding: "var(--space-xl)",
        boxShadow: "var(--shadow-sm)",
      }}>
      <div style={{ fontSize: "13px", fontWeight: "500", color: "var(--text-muted)", marginBottom: "var(--space-md)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </div>
      <div style={{ fontSize: "32px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>{value ?? "—"}</div>
      <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>{subtitle}</div>
    </div>
  );
}
